import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '../components/toast/Toast';
import { Header } from '../components/layout/Header';
import { KpiStrip } from '../components/dashboard/KpiStrip';
import { MandalHeatmap } from '../components/dashboard/MandalHeatmap';
import { AlertsPanel } from '../components/dashboard/AlertsPanel';
import { SchemePipeline } from '../components/dashboard/SchemePipeline';
import { FinancialPhysical } from '../components/dashboard/FinancialPhysical';
import { RecommendationsPanel, ActivityFeed } from '../components/dashboard/RecommendationsAndActivity';
import { MandalDrawer } from '../drawers/MandalDrawer';
import { AlertDrawer } from '../drawers/AlertDrawer';
import { dashboardApi } from '../api/dashboard';
import { exportsApi } from '../api/modules';

export function DashboardPage() {
  const { show } = useToast();
  const [mandalCode, setMandalCode] = useState<string | null>(null);
  const [alertCode, setAlertCode] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Bell badge — show count of open alerts (cheap re-use of cached query)
  const { data: heatmap } = useQuery({
    queryKey: ['dashboard', 'mandal-heatmap'],
    queryFn: dashboardApi.mandalHeatmap,
    staleTime: 60_000,
  });
  const bellCount = heatmap?.reduce((sum, m) => sum + m.open_alerts, 0) ?? 0;

  return (
    <>
      <Header
        breadcrumbs={['Uttar Pradesh — All 75 districts', 'FY 2025-26']}
        bellCount={bellCount}
        onBellClick={() => show('All alerts view coming in Day 5')}
      />

      <div className="px-8 pt-7 pb-2">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">State Operations</div>
            <h1 className="serif-display text-3xl text-navy-800 font-medium">Director's Cockpit</h1>
            <p className="text-sm text-navy-800/60 mt-1">
              All-scheme situational view · Animal Husbandry Department, Uttar Pradesh
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={exporting}
              onClick={async () => {
                setExporting(true);
                show('Generating Director Cockpit PDF…');
                try {
                  const code = await exportsApi.download('/exports/pdf/cockpit', 'director-cockpit.pdf');
                  show(`PDF downloaded · ${code}`);
                } catch {
                  show('Export failed · try again');
                } finally {
                  setExporting(false);
                }
              }}
              className="text-sm px-3 py-2 bg-navy-800 text-white rounded-sm hover:bg-navy-700 disabled:opacity-60 transition flex items-center gap-2"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
              {exporting ? 'Generating…' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        <KpiStrip />

        <section className="grid grid-cols-3 gap-5">
          <MandalHeatmap onCellClick={setMandalCode} />
          <AlertsPanel onAlertClick={setAlertCode} />
        </section>

        <section className="grid grid-cols-2 gap-5">
          <SchemePipeline />
          <FinancialPhysical />
        </section>

        <section className="grid grid-cols-3 gap-5">
          <RecommendationsPanel />
          <ActivityFeed />
        </section>

        <div className="pt-2 pb-4 text-center text-[10px] text-navy-800/40 font-numeric">
          Animal Husbandry Department · Government of Uttar Pradesh · Lucknow · Built by Augurs Technologies
        </div>
      </div>

      <MandalDrawer
        open={!!mandalCode}
        code={mandalCode}
        onClose={() => setMandalCode(null)}
      />
      <AlertDrawer
        open={!!alertCode}
        code={alertCode}
        onClose={() => setAlertCode(null)}
      />
    </>
  );
}
